import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Swords, Clock, Trophy, ArrowRight, Loader2, User, Circle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { QuestionRenderer, OptionRenderer } from "@/components/QuestionRenderer";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

const ALLOWED_REACTIONS = ["🔥", "👍", "😮", "💪", "😅", "🎉"] as const;

interface FloatingReaction {
  id: number;
  emoji: string;
  fromSelf: boolean;
}

interface Question {
  id: string;
  questionType: "multiple-choice" | "multi-select" | "true-false" | "fill-blank" | "order-match" | "table-grid" | "matching";
  topic: string;
  difficulty: "easy" | "medium" | "hard";
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  options?: Array<string | { id?: string; text: string; imageUrl?: string; math?: string }>;
  correctAnswer: string;
  explanation: string;
  explanationImageUrl?: string;
  explanationMath?: string;
  points: number;
}

interface Challenge {
  id: string;
  mode: 'live' | 'async';
  subject: string;
  topic: string | null;
  questionCount: number;
  questionIds: string[];
  status: string;
  creatorId: string;
  opponentId: string;
}

interface ChallengeAttempt {
  id: string;
  challengeId: string;
  userId: string;
  answers: any[];
  totalScore: number;
  correctCount: number;
  currentQuestionIndex: number;
  completedAt: string | null;
}

export default function ChallengeQuiz() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[] | null>(null);
  const [orderedItems, setOrderedItems] = useState<Array<{ id: string; text: string }>>([]);
  const [tapSelectedItem, setTapSelectedItem] = useState<number | null>(null);
  const [matchingSelections, setMatchingSelections] = useState<Record<string, string>>({});
  const [activePrompt, setActivePrompt] = useState<string | null>(null);
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);
  const [presenceState, setPresenceState] = useState<{ [userId: string]: boolean }>({});
  const [challengeState, setChallengeState] = useState<any>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reactionCounterRef = useRef(0);
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);

  // Per-question time tracking
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  const sendReaction = useCallback((emoji: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !id) return;
    ws.send(JSON.stringify({ type: "challenge_reaction", challengeId: id, emoji }));
    // Show self reaction immediately
    const reactId = ++reactionCounterRef.current;
    setFloatingReactions(prev => [...prev, { id: reactId, emoji, fromSelf: true }]);
    setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => r.id !== reactId));
    }, 2000);
  }, [id]);

  // Fetch challenge and start
  const { data: challengeData, isLoading: isLoadingChallenge } = useQuery({
    queryKey: ['/api/challenges', id, 'start'],
    queryFn: async () => {
      const res = await apiRequest('POST', `/api/challenges/${id}/start`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to start challenge');
      }
      return res.json();
    },
    enabled: !!id,
    retry: false
  });

  const challenge = challengeData?.challenge as Challenge | undefined;
  const attempt = challengeData?.attempt as ChallengeAttempt | undefined;

  // If attempt is completed, redirect to results immediately
  useEffect(() => {
    if (attempt?.completedAt) {
      navigate(`/challenge/${id}/results`);
    }
  }, [attempt?.completedAt, id, navigate]);

  // Get current question
  const currentQuestionId = challenge?.questionIds[attempt?.currentQuestionIndex || 0];

  const { data: question, isLoading: isLoadingQuestion } = useQuery<Question>({
    queryKey: ['/api/questions/by-id', currentQuestionId],
    enabled: !!currentQuestionId,
  });

  // Reset question start time and initialize type-specific state when question changes
  useEffect(() => {
    setQuestionStartTime(Date.now());

    if (question?.questionType === 'order-match' && question.options) {
      const items = question.options.map((opt: any, idx: number) => ({
        id: opt.id || `order_${idx}`,
        text: typeof opt === 'string' ? opt : opt.text,
      }));
      setOrderedItems([...items].sort(() => Math.random() - 0.5));
      setTapSelectedItem(null);
    } else {
      setOrderedItems([]);
      setTapSelectedItem(null);
    }

    if (question?.questionType === 'matching') {
      setMatchingSelections({});
      setActivePrompt(null);
    }
  }, [currentQuestionId, attempt?.currentQuestionIndex, question]);

  // Submit answer mutation
  const answerMutation = useMutation({
    mutationFn: async (data: { selectedAnswer: string; timeSpent: number }) => {
      const res = await apiRequest('POST', `/api/challenges/${id}/answer`, {
        questionId: currentQuestionId,
        selectedAnswer: data.selectedAnswer,
        timeSpent: data.timeSpent
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to submit answer');
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data.isComplete) {
        toast({
          title: "Challenge Complete!",
          description: `You scored ${data.attempt.totalScore} points with ${data.attempt.correctCount}/${challenge?.questionCount} correct!`,
        });
        setTimeout(() => {
          navigate(`/challenge/${id}/results`);
        }, 1500);
      } else if (challenge?.mode === 'live' && data.waitingForOpponent) {
        setIsWaitingForOpponent(true);
      } else {
        setSelectedAnswer(null);
        setOrderedItems([]);
        setTapSelectedItem(null);
        setMatchingSelections({});
        setActivePrompt(null);
        queryClient.invalidateQueries({ queryKey: ['/api/challenges', id, 'start'] });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // WebSocket for live mode synchronization
  useEffect(() => {
    if (challenge?.mode !== 'live' || !user || !id) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Authenticate
      ws.send(JSON.stringify({
        type: 'auth',
        userId: user.id
      }));

      // Join challenge session
      setTimeout(() => {
        ws.send(JSON.stringify({
          type: 'challenge_join',
          challengeId: id
        }));
      }, 100);

      // Set up heartbeat
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'challenge_heartbeat',
            challengeId: id
          }));
        }
      }, 5000);
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      
      // Handle presence updates
      if (message.type === 'challenge_presence') {
        const presenceMap: { [userId: string]: boolean } = {};
        message.participants.forEach((p: any) => {
          presenceMap[p.userId] = p.isOnline;
        });
        setPresenceState(presenceMap);
      }
      
      // Handle challenge state updates
      if (message.type === 'challenge_state') {
        setChallengeState(message);
        const myParticipant = message.participants.find((p: any) => p.userId === user.id);
        if (myParticipant && myParticipant.hasAnsweredCurrent) {
          setIsWaitingForOpponent(true);
        }
      }
      
      // Handle question unlock
      if (message.type === 'question_unlocked') {
        setIsWaitingForOpponent(false);
        setSelectedAnswer(null);
        setOrderedItems([]);
        setTapSelectedItem(null);
        setMatchingSelections({});
        setActivePrompt(null);
        queryClient.invalidateQueries({ queryKey: ['/api/challenges', id, 'start'] });
      }
      
      // Handle challenge completion
      if (message.type === 'challenge_completed') {
        queryClient.invalidateQueries({ queryKey: ['/api/challenges', id, 'start'] });
        navigate(`/challenge/${id}/results`);
      }

      // Handle incoming emoji reactions from opponent
      if (message.type === 'challenge_reaction' && message.fromUserId !== user.id) {
        const reactId = ++reactionCounterRef.current;
        setFloatingReactions(prev => [...prev, { id: reactId, emoji: message.emoji, fromSelf: false }]);
        setTimeout(() => {
          setFloatingReactions(prev => prev.filter(r => r.id !== reactId));
        }, 2000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      ws.close();
      wsRef.current = null;
    };
  }, [challenge?.mode, id, user, navigate]);

  const handleSubmitAnswer = () => {
    // Calculate time spent on this question (in seconds)
    const timeSpentMs = Date.now() - questionStartTime;
    const timeSpentSeconds = Math.round(timeSpentMs / 1000);

    let answerToSubmit: string;

    if (question?.questionType === 'order-match') {
      if (orderedItems.length === 0) return;
      answerToSubmit = orderedItems.map(item => item.id).join(',');
    } else if (question?.questionType === 'matching') {
      if (Object.keys(matchingSelections).length === 0) return;
      answerToSubmit = JSON.stringify(matchingSelections);
    } else {
      if (!selectedAnswer) return;
      answerToSubmit = Array.isArray(selectedAnswer)
        ? JSON.stringify([...selectedAnswer].sort())
        : selectedAnswer;
    }

    answerMutation.mutate({
      selectedAnswer: answerToSubmit,
      timeSpent: timeSpentSeconds
    });
  };

  const getOptionLetter = (index: number) => {
    return String.fromCharCode(65 + index); // A, B, C, D...
  };

  if (isLoadingChallenge) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading challenge...</p>
        </div>
      </div>
    );
  }

  if (!challenge || !attempt) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Challenge not found or already completed</p>
            <Button
              onClick={() => navigate('/messages')}
              className="mt-4"
              data-testid="button-back-messages"
            >
              Back to Messages
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cap the displayed question number at questionCount
  const displayedQuestionNumber = Math.min((attempt.currentQuestionIndex || 0) + 1, challenge.questionCount);
  const progress = ((attempt.currentQuestionIndex || 0) / challenge.questionCount) * 100;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4">
          <div className="flex items-center gap-3">
            <Swords className="h-6 w-6 text-primary" />
            <div>
              <CardTitle data-testid="text-challenge-title">Challenge Quiz</CardTitle>
              <div className="flex gap-2 items-center mt-1">
                <Badge variant="outline" data-testid="badge-mode">
                  {challenge.mode === 'live' ? (
                    <><Clock className="h-3 w-3 mr-1" />Live</>
                  ) : (
                    <><Trophy className="h-3 w-3 mr-1" />Async</>
                  )}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {challenge.subject} {challenge.topic && `• ${challenge.topic}`}
                </span>
                {challenge.mode === 'live' && (
                  <div className="flex gap-1 items-center ml-2">
                    <Circle 
                      className={`h-2 w-2 fill-current ${presenceState[challenge.creatorId] ? 'text-green-500' : 'text-gray-400'}`}
                      data-testid="status-creator"
                    />
                    <Circle 
                      className={`h-2 w-2 fill-current ${presenceState[challenge.opponentId] ? 'text-green-500' : 'text-gray-400'}`}
                      data-testid="status-opponent"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Progress */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">Your Progress</span>
              </div>
              <span className="text-sm text-muted-foreground" data-testid="text-your-progress">
                Question {displayedQuestionNumber} of {challenge.questionCount}
              </span>
            </div>
            <Progress value={progress} data-testid="progress-your" />
          </div>
        </CardContent>
      </Card>

      {/* Question */}
      {isLoadingQuestion ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading question...</p>
          </CardContent>
        </Card>
      ) : question ? (
        <Card>
          <CardHeader>
            <QuestionRenderer
              text={question.questionText}
              imageUrl={question.questionImageUrl}
              mathExpression={question.questionMath}
            />
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Multiple Choice / Multi-Select / True-False Options */}
            {(question.questionType === 'multiple-choice' || question.questionType === 'multi-select' || question.questionType === 'true-false') && (
              <div className="space-y-3">
                {question.options?.map((option, index) => {
                  const optionLetter = getOptionLetter(index);
                  const optionValue = typeof option === 'string' ? option : option.text;
                  const optionImage = typeof option === 'object' ? option.imageUrl : undefined;
                  const optionMath = typeof option === 'object' ? option.math : undefined;
                  const answerValue = typeof option === 'object' && option.id ? option.id : optionLetter;

                  const isMultiSelect = question.questionType === 'multi-select';
                  const isSelected = isMultiSelect
                    ? Array.isArray(selectedAnswer) && selectedAnswer.includes(answerValue)
                    : selectedAnswer === answerValue;

                  const handleOptionClick = () => {
                    if (isMultiSelect) {
                      const currentSelections = Array.isArray(selectedAnswer) ? selectedAnswer : [];
                      if (currentSelections.includes(answerValue)) {
                        const newSelections = currentSelections.filter((id: string) => id !== answerValue);
                        setSelectedAnswer(newSelections.length > 0 ? newSelections : null);
                      } else {
                        setSelectedAnswer([...currentSelections, answerValue]);
                      }
                    } else {
                      setSelectedAnswer(answerValue);
                    }
                  };

                  return (
                    <button
                      key={index}
                      onClick={handleOptionClick}
                      disabled={answerMutation.isPending}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all hover-elevate active-elevate-2 ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      } ${answerMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`option-${optionLetter.toLowerCase()}`}
                    >
                      <div className="flex items-start gap-3">
                        {isMultiSelect && (
                          <Checkbox
                            checked={isSelected}
                            className="mt-1 pointer-events-none"
                            data-testid={`checkbox-${optionLetter.toLowerCase()}`}
                          />
                        )}
                        <div className="flex-1">
                          <OptionRenderer
                            text={optionValue}
                            imageUrl={optionImage}
                            mathExpression={optionMath}
                            optionLetter={optionLetter}
                          />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Fill-in-the-blank */}
            {question.questionType === 'fill-blank' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Type your answer:</p>
                <input
                  type="text"
                  value={typeof selectedAnswer === 'string' ? selectedAnswer : ''}
                  onChange={(e) => setSelectedAnswer(e.target.value || null)}
                  disabled={answerMutation.isPending}
                  placeholder="Your answer..."
                  className="w-full px-4 py-3 rounded-lg border-2 border-muted focus:border-primary text-base transition-all outline-none bg-background"
                  data-testid="input-fill-blank"
                  onKeyDown={(e) => { if (e.key === 'Enter' && selectedAnswer) handleSubmitAnswer(); }}
                />
              </div>
            )}

            {/* Order Match: tap to swap */}
            {question.questionType === 'order-match' && orderedItems.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Tap an item to select it, then tap another to swap positions.</p>
                {orderedItems.map((item, index) => {
                  const isTapSelected = tapSelectedItem === index;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (answerMutation.isPending) return;
                        if (tapSelectedItem === null) {
                          setTapSelectedItem(index);
                        } else if (tapSelectedItem === index) {
                          setTapSelectedItem(null);
                        } else {
                          const newItems = [...orderedItems];
                          [newItems[tapSelectedItem], newItems[index]] = [newItems[index], newItems[tapSelectedItem]];
                          setOrderedItems(newItems);
                          setTapSelectedItem(null);
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all cursor-pointer ${
                        isTapSelected ? 'bg-primary/10 border-primary ring-2 ring-primary/50' : 'bg-background border-muted hover:border-primary/50'
                      } ${answerMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`order-item-${index}`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        isTapSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 text-sm text-left">{item.text}</div>
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Matching: tap prompt then tap answer */}
            {question.questionType === 'matching' && question.options?.[0] && typeof question.options[0] === 'object' && 'prompts' in question.options[0] && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Match each item on the left with the correct answer on the right.</p>
                {(() => {
                  const data = question.options![0] as any;
                  const prompts = data.prompts || {};
                  const answers = data.answers || {};
                  const promptKeys = Object.keys(prompts);
                  const answerKeys = Object.keys(answers);
                  // Track which answers are already used
                  const usedAnswers = new Set(Object.values(matchingSelections));

                  return (
                    <div className="space-y-3">
                      {promptKeys.map((pKey) => {
                        const isActive = activePrompt === pKey;
                        const selectedAKey = matchingSelections[pKey];
                        return (
                          <div key={pKey} className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                if (answerMutation.isPending) return;
                                setActivePrompt(isActive ? null : pKey);
                              }}
                              className={`flex-1 p-3 border-2 rounded-lg text-sm text-left transition-all ${
                                isActive ? 'border-primary bg-primary/5' : 'border-border'
                              }`}
                            >
                              {prompts[pKey]}
                            </button>
                            <span className="text-muted-foreground">→</span>
                            {selectedAKey ? (
                              <button
                                onClick={() => {
                                  if (answerMutation.isPending) return;
                                  const newSelections = { ...matchingSelections };
                                  delete newSelections[pKey];
                                  setMatchingSelections(newSelections);
                                }}
                                className="flex-1 p-3 border-2 border-primary bg-primary/5 rounded-lg text-sm text-left"
                              >
                                {answers[selectedAKey]}
                              </button>
                            ) : (
                              <div className="flex-1 p-3 border-2 border-dashed border-muted rounded-lg text-sm text-muted-foreground">
                                {isActive ? 'Select an answer →' : 'Tap to match'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {activePrompt && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Choose an answer:</p>
                          <div className="flex flex-wrap gap-2">
                            {answerKeys.filter(aKey => !usedAnswers.has(aKey) || matchingSelections[activePrompt] === aKey).map((aKey) => (
                              <button
                                key={aKey}
                                onClick={() => {
                                  if (answerMutation.isPending) return;
                                  setMatchingSelections({ ...matchingSelections, [activePrompt]: aKey });
                                  // Auto-advance to next unmatched prompt
                                  const nextUnmatched = promptKeys.find(pk => pk !== activePrompt && !matchingSelections[pk]);
                                  setActivePrompt(nextUnmatched || null);
                                }}
                                className="px-3 py-2 border-2 border-border rounded-lg text-sm hover:border-primary transition-all"
                              >
                                {answers[aKey]}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Waiting state or Submit Button */}
            {isWaitingForOpponent && challenge.mode === 'live' ? (
              <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span data-testid="text-waiting-opponent">Waiting for opponent to answer...</span>
              </div>
            ) : (
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={
                    answerMutation.isPending || isWaitingForOpponent ||
                    (question.questionType === 'order-match' ? orderedItems.length === 0 :
                     question.questionType === 'matching' ? Object.keys(matchingSelections).length === 0 :
                     !selectedAnswer)
                  }
                  size="lg"
                  data-testid="button-submit-answer"
                >
                  {answerMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Next Question
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Question not found</p>
          </CardContent>
        </Card>
      )}

      {/* Emoji reaction bar: live mode only */}
      {challenge?.mode === 'live' && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex gap-2 bg-background/90 backdrop-blur border rounded-full px-4 py-2 shadow-lg">
            {ALLOWED_REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => sendReaction(emoji)}
                className="text-xl hover:scale-125 transition-transform active:scale-95"
                title={`Send ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Floating emoji animations */}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
        <AnimatePresence>
          {floatingReactions.map(r => (
            <motion.div
              key={r.id}
              initial={{ opacity: 1, y: 0, scale: 1 }}
              animate={{ opacity: 0, y: -80, scale: 1.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.8 }}
              className="absolute text-3xl select-none"
              style={{ left: r.fromSelf ? "-20px" : "20px" }}
            >
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
